import type {
  QuestionResponsesSearchFilter,
  QuestionValue,
  QuestionVariable,
  QuestionVariablesResponse,
} from "@models/Filter";
import type {
  VisualizationBase,
  VisualizationBaseCondition,
} from "@models/VisualizationBase";
import { getVisualizationBaseSource } from "@models/VisualizationBase";

export type BaseQuestionShape = "single" | "multiple" | "grid";

/**
 * Single grid is not a distinct API question type — it's a "single" question
 * with more than one variable (e.g. statements x rating scale).
 */
export function classifyBaseQuestionShape(
  questionType: "single" | "multi" | null | undefined,
  variableCount: number,
): BaseQuestionShape | null {
  if (questionType === "multi") return "multiple";
  if (questionType === "single") return variableCount > 1 ? "grid" : "single";
  return null;
}

// Human-readable question type, shown next to the question name in both the
// question selector dropdown and the saved condition summary cards.
export const BASE_QUESTION_SHAPE_LABEL: Record<BaseQuestionShape, string> = {
  single: "Single",
  multiple: "Multiple",
  grid: "Single grid",
};

export function getVariableLabel(variable: QuestionVariable): string {
  return variable.rowtitle || variable.title || variable.label;
}

export function getValueLabel(value: QuestionValue): string {
  return value.title || value.label;
}

/**
 * True when the only difference between the previous and next base is the
 * `name` field — a purely cosmetic label that doesn't affect the resolved
 * filters. Callers use this to skip refetching chart data on every keystroke
 * while editing a Custom Base's name.
 */
export function isOnlyBaseNameChanged(
  prev: VisualizationBase | undefined,
  next: VisualizationBase,
): boolean {
  if (!prev) return false;
  return (
    prev.name !== next.name &&
    prev.enabled === next.enabled &&
    prev.source === next.source &&
    prev.preconfiguredBaseId === next.preconfiguredBaseId &&
    JSON.stringify(prev.conditions) === JSON.stringify(next.conditions)
  );
}

export function isBaseConditionValid(
  condition: VisualizationBaseCondition,
  shape: BaseQuestionShape | null,
): boolean {
  if (!condition.questionId || !shape) return false;
  if (shape === "multiple") return condition.variableIds.length > 0;
  if (shape === "single") return condition.valueIds.length > 0;
  return condition.variableIds.length > 0 && condition.valueIds.length > 0;
}

// Each condition can only enter base.conditions via the builder's own
// Save action, which already gates on isBaseConditionValid — so every
// persisted condition is guaranteed valid by construction here.
export function isVisualizationBaseValid(
  base: VisualizationBase | undefined,
): boolean {
  if (!base?.enabled) return true;
  // Pre-configured: valid once a Dataset-Level Base is selected. Its conditions
  // live on the dataset, so there's nothing else to validate widget-side.
  if (getVisualizationBaseSource(base) === "preconfigured") {
    return !!base.preconfiguredBaseId;
  }
  return !!base.name.trim() && base.conditions.length > 0;
}

/**
 * Transforms an enabled visualizationBase into /search-ready filters, using
 * the same QuestionResponsesSearchFilter contract and variable.label /
 * String(value.value) identifier convention as dashboard filters and bar
 * chart focus/rollup (see DashboardFilters.tsx's transform and
 * filter-mapping.ts) — never datasetElementId. Each condition becomes its
 * own filter entry; the backend ANDs separate entries by default, so no
 * extra grouping key is needed.
 *
 * Single grid conditions (both a variable and a value selected) use
 * filterSubtype "VARIABLE" with selected value strings in `selectedValues`
 * and selected variable labels in `variables` — same convention as the
 * variable-focus case (variables carry the `variables` field, the value
 * axis rides in `selectedValues`), extended to also carry the value
 * restriction.
 *
 * `resolvedVariablesByQuestionId` must already contain each condition's
 * question's variables/values (fetched separately, since resolving IDs to
 * labels requires an API call this function can't make itself).
 */
export function visualizationBaseToSearchFilters(
  base: VisualizationBase | undefined | null,
  resolvedVariablesByQuestionId: Record<string, QuestionVariablesResponse>,
): QuestionResponsesSearchFilter[] {
  if (!base?.enabled) return [];

  const filters: QuestionResponsesSearchFilter[] = [];

  for (const condition of base.conditions) {
    const resolved = resolvedVariablesByQuestionId[condition.questionId];
    if (!resolved) continue;

    const hasVariables = condition.variableIds.length > 0;
    const hasValues = condition.valueIds.length > 0;
    if (!hasVariables && !hasValues) continue;

    const variableLabels = hasVariables
      ? (resolved.variables ?? [])
          .filter((v) => condition.variableIds.includes(v.datasetElementId))
          .map((v) => v.label)
          .filter((label): label is string => !!label)
      : [];
    const valueStrings = hasValues
      ? (resolved.values ?? [])
          .filter((v) => condition.valueIds.includes(v.datasetElementId))
          .map((v) => String(v.value))
          .filter((val): val is string => !!val)
      : [];

    // "MULTI" — not "MULTIPLE", despite the QuestionResponsesSearchFilter
    // type declaration saying otherwise; that type is stale.
    const questionType = (
      condition.questionType === "multi" ? "MULTI" : "SINGLE"
    ) as unknown as QuestionResponsesSearchFilter["questionType"];

    if (hasVariables && hasValues) {
      // Single grid — cross filter (see function doc comment).
      if (variableLabels.length === 0 || valueStrings.length === 0) continue;
      filters.push({
        sourceQuestionId: condition.questionId,
        questionType,
        sourceType: "QUESTION",
        controlType: "MULTI",
        filterSubtype: "VARIABLE",
        selectedValues: valueStrings,
        values: [],
        variables: variableLabels,
      });
    } else if (hasVariables) {
      if (variableLabels.length === 0) continue;
      filters.push({
        sourceQuestionId: condition.questionId,
        questionType,
        sourceType: "QUESTION",
        controlType: "MULTI",
        filterSubtype: null,
        selectedValues: variableLabels,
        values: [],
        variables: [],
      });
    } else {
      if (valueStrings.length === 0) continue;
      filters.push({
        sourceQuestionId: condition.questionId,
        questionType: "SINGLE",
        sourceType: "QUESTION",
        controlType: "MULTI",
        filterSubtype: null,
        selectedValues: valueStrings,
        values: [],
        variables: [],
      });
    }
  }

  return filters;
}

function sameElements(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((v) => setB.has(v));
}

/**
 * Two filters are considered an exact match when they target the same
 * question and carry the same selectedValues/values/variables (order
 * doesn't matter — compared as sets).
 */
function isSameSearchFilter(
  a: QuestionResponsesSearchFilter,
  b: QuestionResponsesSearchFilter,
): boolean {
  return (
    a.sourceQuestionId === b.sourceQuestionId &&
    sameElements(a.selectedValues ?? [], b.selectedValues ?? []) &&
    sameElements(a.values ?? [], b.values ?? []) &&
    sameElements(a.variables ?? [], b.variables ?? [])
  );
}

/**
 * Merges base-derived filters into an existing filter list (typically the
 * active dashboard filters), dropping any base filter that's an exact match
 * for one already present — same source question, same selectedValues,
 * values, and variables. Dashboard filters always win the dedup (they're
 * passed through untouched); only the redundant base filter is dropped, so
 * the same qualification is never sent twice.
 */
export function mergeBaseFiltersWithExistingFilters(
  existingFilters: QuestionResponsesSearchFilter[],
  baseFilters: QuestionResponsesSearchFilter[],
): QuestionResponsesSearchFilter[] {
  const dedupedBaseFilters = baseFilters.filter(
    (baseFilter) =>
      !existingFilters.some((existing) =>
        isSameSearchFilter(existing, baseFilter),
      ),
  );
  return [...existingFilters, ...dedupedBaseFilters];
}
